import { Dtos, naicsCodeSchema, SuretyAccountUnderwritingStatus } from "@inrev/common";
import {
	useAdminDeclineAccount,
	useAdminFetchAccountCompanies,
	useAdminFetchAccountIndividuals,
	useAdminFetchAccountSpecialtyCodes,
	useAdminUpdateSuretyAccountBondingLines,
	useAdminUpdateSuretyAccountStatus,
} from "@portal/api/account/admin";
import { getUwModelDisplayName } from "@portal/api/account/admin/utils";
import { useLogDecisionNudge } from "@portal/components/features/decisioning";
import { useRecentFyeRevenue } from "@portal/components/financials";
import { ConfirmationModalClickWrapper } from "@portal/components/layout/ConfirmationModalClickWrapper";
import { LoadingModal } from "@portal/components/layout/LoadingModal";
import { Icon, Text } from "@portal/components/primitives";
import { Button } from "@portal/components/primitives/Button";
import { getStatusTagByStatus } from "@portal/components/ui/StatusTag";
import { PortaledTooltip } from "@portal/components/ui/Tooltip";
import { useFlags } from "@portal/providers/GlobalFlagsProvider";
import { DateTime } from "luxon";
import { ReactNode, useId, useMemo } from "react";
import type { AccountIdentity } from "../../../../../shared/account/AccountHeader";
import { AdminUpdateUnderwritingExpirationModal } from "../AdminUpdateUnderwritingExpirationModal";
import { BondingLinePublishControl } from "./BondingLinePublishControl";
import { buildApprovePayload, buildBondingLinesPayload } from "./payload";
import { useBondingLineDispatch, useBondingLineState } from "./state/BondingLineStateProvider";
import { anyLineHasError, isStateDirty, shouldShowLineErrors } from "./state/reducer";

const BONDING_LINE_RULES =
	"Each line needs a rate between 0% and 100%, non-negative limits, and an aggregate at or above the single limit.";

const INVALID_BONDING_LINES_FLAG = {
	heading: "Invalid bonding lines",
	message: BONDING_LINE_RULES,
};

const Stat = ({ label, children }: { label: string; children: ReactNode }) => (
	<div className="flex flex-col gap-y-2">
		<Text type="eyebrow" color="tertiary" className="[text-box-trim:none]">
			{label}
		</Text>
		{children}
	</div>
);

/**
 * Shows `message` as a hover/focus tooltip explaining why the wrapped (disabled) action is
 * blocked. `[&_button]:pointer-events-none` lets the pointer reach the span so the tooltip
 * fires even though a disabled button is otherwise inert. Renders children untouched when
 * there's no message.
 */
const ValidationHint = ({ message, children }: { message: string | null; children: ReactNode }) => {
	const tooltipId = useId();
	if (!message) return <>{children}</>;
	return (
		<span
			tabIndex={0}
			aria-label={message}
			data-testid="bonding-lines-validation"
			data-tooltip-id={tooltipId}
			data-tooltip-content={message}
			className="inline-flex cursor-not-allowed [&_button]:pointer-events-none"
		>
			{children}
			<PortaledTooltip id={tooltipId} style={{ maxWidth: 260 }} />
		</span>
	);
};

type BondingLineApprovalStripProps = {
	account: Pick<AccountIdentity, "id" | "status">;
	underwriting: Dtos.Admin.Account.Underwriting.Get.Response;
};

export const BondingLineApprovalStrip = ({
	account,
	underwriting,
}: BondingLineApprovalStripProps) => {
	const state = useBondingLineState();
	const dispatch = useBondingLineDispatch();
	const { showErrorFlag } = useFlags();

	const { fyeRevenue, isLoading: financialsLoading } = useRecentFyeRevenue(account.id);
	const {
		rows: specialtyRows,
		isLoading: specialtyLoading,
		error: specialtyError,
	} = useAdminFetchAccountSpecialtyCodes(account.id);
	const { companies, companiesError, companiesIsLoading } = useAdminFetchAccountCompanies(
		account.id,
	);
	const { individuals, individualsError, individualsIsLoading } = useAdminFetchAccountIndividuals(
		account.id,
	);
	const modelInputsUnavailable = Boolean(companiesError || individualsError || specialtyError);
	const modelInputsLoading =
		companiesIsLoading || individualsIsLoading || specialtyLoading || financialsLoading;
	const uwModelDisplayName = getUwModelDisplayName(
		{
			isContractSurety: true,
			companies: companies ?? [],
			individuals: individuals ?? [],
		},
		fyeRevenue,
		underwriting.modelSize,
		naicsCodeSchema.array().parse(specialtyRows.map((row) => row.code)),
	);

	const { updateSuretyAccountStatus, updateSuretyAccountStatusIsLoading } =
		useAdminUpdateSuretyAccountStatus(account.id, underwriting.id);
	const { updateSuretyAccountBondingLines, updateSuretyAccountBondingLinesIsLoading } =
		useAdminUpdateSuretyAccountBondingLines(account.id);
	const nudgeLogDecision = useLogDecisionNudge();
	const { declineAccount, declineAccountLoading } = useAdminDeclineAccount(
		account.id,
		underwriting.id,
	);

	const isApproved = underwriting.status === SuretyAccountUnderwritingStatus.approved;
	const isDeclined = underwriting.status === SuretyAccountUnderwritingStatus.declined;
	const score = underwriting.score;
	const completeness = underwriting.completenessScore;

	const hasLineError = useMemo(() => anyLineHasError(state), [state]);

	const rejectSubmission = () => {
		dispatch({ type: "mark_submit_attempted" });
		showErrorFlag(INVALID_BONDING_LINES_FLAG);
	};

	const onApprove = () => {
		const payload = buildApprovePayload(state);
		if (hasLineError || !payload) {
			rejectSubmission();
			return;
		}
		updateSuretyAccountStatus(payload, { onSuccess: () => nudgeLogDecision("approve") });
	};
	const onDecline = () =>
		declineAccount(undefined, { onSuccess: () => nudgeLogDecision("decline") });
	const onSave = () => {
		const payload = buildBondingLinesPayload(state);
		if (hasLineError || !payload) {
			rejectSubmission();
			return;
		}
		updateSuretyAccountBondingLines(payload);
	};

	const isLoading =
		updateSuretyAccountStatusIsLoading ||
		updateSuretyAccountBondingLinesIsLoading ||
		declineAccountLoading;

	const validationMessage = shouldShowLineErrors(state) && hasLineError ? BONDING_LINE_RULES : null;

	return (
		<div className="flex items-end justify-between w-full gap-x-8 flex-wrap gap-y-4">
			<div className="flex items-start gap-x-12">
				<Stat label="Status">{getStatusTagByStatus(underwriting.status, { condensed: true })}</Stat>
				<Stat label="Expiration">
					<AdminUpdateUnderwritingExpirationModal
						accountId={account.id}
						underwritingId={underwriting.id}
						expiresAt={
							underwriting.expiresAt
								? DateTime.fromISO(underwriting.expiresAt, { zone: "utc" })
								: null
						}
					/>
				</Stat>
				<Stat label="Score">
					<Text type="body" color="primary" className="[text-box-trim:none]">
						{score === undefined
							? "--"
							: `${score}${completeness != null && completeness < 100 ? ` (${completeness}%)` : ""}`}
					</Text>
				</Stat>
				<Stat label="Model">
					<Text
						type="body"
						color="primary"
						data-testid="uw-model-display-name"
						className="[text-box-trim:none]"
					>
						{modelInputsLoading || modelInputsUnavailable ? "--" : uwModelDisplayName}
					</Text>
				</Stat>
				<Stat label="Visibility">
					<BondingLinePublishControl accountId={account.id} />
				</Stat>
			</div>

			<div className="flex items-center gap-x-3">
				{state.mode === "review" && (
					<>
						{!isDeclined && (
							<ConfirmationModalClickWrapper
								message="Decline this account?"
								onConfirm={onDecline}
								confirmTrackingId="account-admin-bonding-lines-decline-confirm"
								cancelTrackingId="account-admin-bonding-lines-decline-cancel"
							>
								<Button
									color="danger"
									className="w-fit"
									data-testid="decline-button"
									trackingId="account-admin-bonding-lines-decline"
								>
									Decline
								</Button>
							</ConfirmationModalClickWrapper>
						)}
						<ValidationHint message={validationMessage}>
							<ConfirmationModalClickWrapper
								message="Approve this account?"
								onConfirm={onApprove}
								confirmTrackingId="account-admin-bonding-lines-approve-confirm"
								cancelTrackingId="account-admin-bonding-lines-approve-cancel"
							>
								{/* Only block when the persisted lines are invalid and untouched: clicking then
								    couldn't reveal anything new. Once dirty, approve stays clickable so the attempt
								    surfaces row errors. */}
								<Button
									color="confirm"
									disabled={hasLineError && !isStateDirty(state)}
									className="w-fit"
									data-testid="approve-button"
									trackingId="account-admin-bonding-lines-approve"
								>
									Approve
								</Button>
							</ConfirmationModalClickWrapper>
						</ValidationHint>
					</>
				)}

				{state.mode === "readonly" && isApproved && (
					<Button
						color="inverse"
						className="w-fit"
						onClick={() => dispatch({ type: "enter_edit" })}
						data-testid="edit-button"
						trackingId="account-admin-bonding-lines-edit"
						leadingIcon={<Icon type="pencil-square" size="mini" color="primary" />}
					>
						Edit rates & limits
					</Button>
				)}

				{state.mode === "editing" && (
					<>
						<Button
							color="secondary"
							className="w-fit"
							onClick={() => dispatch({ type: "cancel_edit" })}
							data-testid="cancel-button"
							trackingId="account-admin-bonding-lines-cancel"
						>
							Cancel
						</Button>
						<ValidationHint message={validationMessage}>
							<ConfirmationModalClickWrapper
								message="Save bonding line changes?"
								onConfirm={onSave}
								confirmTrackingId="account-admin-bonding-lines-save-confirm"
								cancelTrackingId="account-admin-bonding-lines-save-cancel"
							>
								<Button
									color="primary"
									disabled={!isStateDirty(state)}
									className="w-fit"
									data-testid="save-button"
									trackingId="account-admin-bonding-lines-save"
								>
									Save
								</Button>
							</ConfirmationModalClickWrapper>
						</ValidationHint>
					</>
				)}
			</div>

			{isLoading && <LoadingModal />}
		</div>
	);
};
